import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Box,
  Tabs,
  Tab,
  FormControl,
  styled
} from '@mui/material';
import { useCallback, useState, useRef, useEffect, Suspense } from 'react';
import { useForm } from 'react-hook-form';

import {
  filterEmptyDescriptions,
  filterEmptyAgeGroups
} from '../../utils/validators';
import { fileToBase64 } from '../../utils/fileHandlers';
import GeneralPanel from './GeneralPanel';
import TestListPanel from './TestListPanel';
import SamplePicturePanel from './SamplePicturePanel';
import TestSettingsPanel from './TestSettingsPanel';
import JobDialogProps from './JobDialogProps';
import GeneratePDFDialog from '../GeneratePDFDialog';
import { MissingJobFields } from './RequiredFieldsDialogProps';
import RequiredFieldsDialog from './RequiredFieldsDialog';
import { useAuthentication } from '../../api/authentication';
import AlertDialog from '../AlertDialog';
import { useGetProctorsQuery } from '../../api/proctors/GetProctorsQuery';
import {
  defaultAdultMaxParticipantsPerProctor,
  defaultAdultMaxParticipantsPerSite
} from '../../models/Job';
import { JobFormValues } from './JobFormValues';
import ParticipantsPanel from './ParticipantsPanel';

const StyledDialog = styled(Dialog)`
  .MuiPaper-root {
    height: calc(100% - 64px);
  }
`;

const StyledDialogContent = styled(DialogContent)`
  display: flex;
  flex-direction: column;
`;

const TabBar = styled(Box)`
  border-bottom: 1;
  border-color: rgba(0, 0, 0, 0.23);
`;

const PanelActions = styled('div')`
  flex: 1;
`;

const addId = (row: any, index: number) => {
  const newRow = { id: String(index), ...row };
  return newRow;
};

const removeId = (row: any) => {
  const { id, ...newRow } = row;
  return newRow;
};

const JobDialog = ({
  job,
  jobs,
  categories,
  onCancel,
  onSubmit,
  sites
}: JobDialogProps) => {
  const [participantData, setParticipantData] = useState<any[]>(
    job?.participants
      ? job.participants.map((p, idx) => ({ ...p, id: String(idx) }))
      : []
  );
  const [newParticipantData, setNewParticipantData] = useState<any[]>([]);

  const participantsPanelRef = useRef<any>(null);

  let childKey = '';
  if (job?.children && job?.children.length) {
    childKey = job.children[0].key;
  }
  const { user } = useAuthentication();
  const [currentTab, setCurrentTab] = useState(0);
  const [parentJobKey, setParentJobKey] = useState(job?.parent?.key || null);
  const [showJobMissingFields, setShowJobMissingFields] = useState(false);
  const [missingFields, setMissingFields] = useState<MissingJobFields | null>(
    null
  );
  type queryOptions = {
    fetchKey: number;
    fetchPolicy: string;
  };
  const [
    refreshedParticipantQueryOptions,
    setRefreshedParticipantQueryOptions
  ] = useState<queryOptions>({
    fetchKey: 0,
    fetchPolicy: 'network-only'
  });
  const [showGeneratePDFDialog, setShowGeneratePDFDialog] = useState(false);
  const [showJobCategoryChangedDialog, setShowJobCategoryChangedDialog] =
    useState(false);

  const proctors = useGetProctorsQuery(refreshedParticipantQueryOptions ?? {});

  // state is managed here for manually refreshing the participants data within the participants component.
  // see documentation here: https://relay.dev/docs/guided-tour/refetching/refreshing-queries/#when-using-uselazyloadquery
  const refreshParticipants = useCallback(() => {
    setRefreshedParticipantQueryOptions((prev) => ({
      fetchKey: (prev.fetchKey ?? 0) + 1,
      fetchPolicy: 'network-only'
    }));
  }, []);

  const defaultValues: JobFormValues = {
    // General Tab
    key: job?.key || '',
    description: job?.description || '',
    customerId: job?.customer?.id || '',
    jobCategoryId: job?.jobCategoryId || '',
    regionIds: job?.regions
      ? job.regions.map((region) => region.id as string).filter(Boolean)
      : [],
    status: job?.status || 'Tentative',
    relatedJob: childKey || '',
    preparedById: job?.preparedBy?.id || '',
    endDate: job?.endDate
      ? new Date(job.endDate).toISOString().split('T')[0]
      : '',
    notes: job?.notes || '',

    // Test Settings Tab
    sampleType: job?.sampleType || '',
    submitter: job?.submitter || '',
    testType: job?.testType || 'Adult',
    participantType: job?.participantType || '',
    requiredParticipants: job?.requiredParticipants || 0,
    numberOfParticipants: job?.numberOfParticipants || 0,
    maxParticipantsPerSite:
      job?.maxParticipantsPerSite ?? defaultAdultMaxParticipantsPerSite,
    maxParticipantsPerProctor:
      job?.maxParticipantsPerProctor ?? defaultAdultMaxParticipantsPerProctor,
    customerTest: job?.customerTest || '',
    reportTree: job?.reportTree || 0,
    testObjectId: job?.testObjectId || '',
    requiredTorque: job?.requiredTorque || 0,
    updatedAt: job?.updatedAt
      ? new Date(job?.updatedAt).toLocaleDateString()
      : '',
    updatedBy: job?.updatedBy?.username || '',
    disclaimer: job?.disclaimer || '',
    otherInfo: job?.otherInfo || '',
    reportHistory: job?.reportHistory || '',
    testDescription: job?.testDescription || '',
    objectives: job?.objectives || '',
    procedureNotes: job?.procedureNotes || '',
    resultsDiscussion: job?.resultsDiscussion || '',

    // Sample Picture Tab
    samplePicture: job?.samplePicture || null,

    // Test Lists Tab
    methodsOfUse: job ? [...job.methodsOfUse.map(addId)] : [],
    procedures: job ? [...job.procedures.map(addId)] : [],
    easeOfUseRatings: job ? [...job.easeOfUseRatings.map(addId)] : [],
    ageGroups: job ? [...job.ageGroups.map(addId)] : []
  };

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors }
  } = useForm<JobFormValues>({ defaultValues });

  useEffect(() => {
    reset(defaultValues);
  }, [job]);

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!event.target.files || !event.target.files[0]) {
      return;
    }
    const file = event.target.files[0];
    const base64String = await fileToBase64(file);
    setValue('samplePicture', base64String);
  };
  const customerJobs = jobs.filter((evalJob) => {
    return (
      evalJob.customerId === `Customer_${job?.customerId}` &&
      evalJob.id !== job?.id
    );
  });
  const possibleRelatedJobs = customerJobs.map((jobObj) => {
    return { id: jobObj.id ? jobObj.id : '', key: jobObj.key };
  });

  const checkMissingFields = (data: any) => {
    const missingRequiredFields: MissingJobFields = {
      general: [],
      testSettings: [],
      testLists: [],
      samplePicture: [],
      participants: []
    };
    let isMissingFields = false;
    if (!data.key) {
      missingRequiredFields.general.push('Key');
      isMissingFields = true;
    }
    if (!data.customerId) {
      missingRequiredFields.general.push('Customer');
      isMissingFields = true;
    }
    if (!data.jobCategoryId) {
      missingRequiredFields.general.push('Category');
      isMissingFields = true;
    }
    if (!data.preparedById) {
      missingRequiredFields.general.push('Prepared By');
      isMissingFields = true;
    }
    if (!data.regionIds || data.regionIds.length === 0) {
      missingRequiredFields.general.push('Region');
      isMissingFields = true;
    }
    if (!data.status) {
      missingRequiredFields.general.push('Status');
      isMissingFields = true;
    }
    return {
      isMissingFields: isMissingFields,
      missingFields: missingRequiredFields
    };
  };

  // Our permission architecture currently does not support having permission
  // to view only parts of an object, so we must manually check here.
  const hasViewPermission = user?.role === 'admin' || user?.role === 'sales';

  const formJobCategoryId = watch('jobCategoryId');
  const jobCategory = categories.find(
    (category) => category.id === formJobCategoryId
  );

  return (
    <>
      {job && showGeneratePDFDialog && (
        <GeneratePDFDialog
          jobId={job.id}
          reportType={jobCategory?.reportType}
          onCancel={() => setShowGeneratePDFDialog(false)}
          onSubmit={() => setShowGeneratePDFDialog(false)}
          participantData={job.participants}
          sites={sites}
          proctors={proctors}
          job={job}
        />
      )}
      {showJobCategoryChangedDialog && (
        <AlertDialog
          title='Job Category Changed'
          content="Do you want to update this job to use the new job category's test list settings?"
          cancelLabel='No'
          submitLabel='Yes'
          onCancel={() => setShowJobCategoryChangedDialog(false)}
          onSubmit={() => {
            const jobCategory = categories.find(
              (category) => category.id === watch('jobCategoryId')
            );
            if (jobCategory) {
              setValue('methodsOfUse', [...jobCategory.methodsOfUse]);
              setValue('procedures', [...jobCategory.procedures]);
              setValue('easeOfUseRatings', [...jobCategory.easeOfUseRatings]);
              setValue('ageGroups', [...jobCategory.ageGroups]);
            }
            setShowJobCategoryChangedDialog(false);
          }}
        />
      )}
      <StyledDialog fullScreen open>
        <DialogTitle>{job ? `Test ID: ${job.key}` : 'Add Job'}</DialogTitle>
        <StyledDialogContent>
          <TabBar>
            <Tabs
              value={currentTab}
              onChange={(_event, value) => setCurrentTab(value)}
              aria-label='tabs'
            >
              {hasViewPermission && <Tab label='General' value={0} />}
              {hasViewPermission && <Tab label='Test Settings' value={1} />}
              {hasViewPermission && <Tab label='Test Lists' value={2} />}
              {hasViewPermission && <Tab label='Sample Picture' value={3} />}
              <Tab label='Participants' value={hasViewPermission ? 4 : 0} />
            </Tabs>
          </TabBar>
          {hasViewPermission && currentTab === 0 && (
            <GeneralPanel
              register={register}
              errors={errors}
              control={control}
              categories={categories}
              onJobCategoryChange={() => setShowJobCategoryChangedDialog(true)}
              parentJobKey={parentJobKey}
              setParentJobKey={setParentJobKey}
              jobs={possibleRelatedJobs}
            />
          )}
          {hasViewPermission && currentTab === 1 && (
            <TestSettingsPanel
              register={register}
              errors={errors}
              control={control}
              numberOfParticipants={job?.participants?.length ?? 0}
              setValue={setValue}
            />
          )}
          {hasViewPermission && currentTab === 2 && (
            <TestListPanel control={control} setValue={setValue} />
          )}
          {hasViewPermission && currentTab === 3 && (
            <SamplePicturePanel control={control} />
          )}
          {currentTab === (hasViewPermission ? 4 : 0) && (
            <Suspense fallback='Loading Participant Records'>
              <ParticipantsPanel
                ref={participantsPanelRef}
                job={job}
                sites={sites}
                proctors={proctors}
                refreshParticipants={refreshParticipants}
                queryOptions={refreshedParticipantQueryOptions ?? {}}
                participantData={participantData}
                setParticipantData={setParticipantData}
                newParticipantData={newParticipantData}
                setNewParticipantData={setNewParticipantData}
              />
            </Suspense>
          )}
        </StyledDialogContent>
        <DialogActions>
          <PanelActions>
            <Button
              onClick={async () => {
                if (
                  participantsPanelRef.current &&
                  participantsPanelRef.current.saveAllChanges
                ) {
                  await participantsPanelRef.current.saveAllChanges();
                }
                setShowGeneratePDFDialog(true);
              }}
            >
              Generate Report
            </Button>
            {currentTab === 3 && (
              <FormControl margin='none'>
                <input
                  style={{ display: 'none' }}
                  id='contained-button-file'
                  type='file'
                  accept='image/png, image/jpeg'
                  onChange={handleFileUpload}
                />
                <label htmlFor='contained-button-file'>
                  <Button color='primary' component='span'>
                    Upload
                  </Button>
                </label>
              </FormControl>
            )}
          </PanelActions>
          <Button onClick={onCancel}>
            {user?.role === 'admin' ? 'Cancel' : 'Close'}
          </Button>
          {(user?.role === 'admin' || user?.role === 'proctor') && (
            <Button
              onClick={handleSubmit(async (fields) => {
                if (
                  participantsPanelRef.current &&
                  participantsPanelRef.current.saveAllChanges
                ) {
                  await participantsPanelRef.current.saveAllChanges();
                }
                if (user?.role === 'proctor') {
                  return;
                }

                const relatedJobs = jobs.filter(
                  (eachJob) => eachJob.key === fields.relatedJob
                );
                const childJobIds =
                  relatedJobs.length > 0 ? [relatedJobs[0].id] : [];
                const dataToSubmit = {
                  ...fields,
                  id: job?.id,
                  endDate: new Date(fields.endDate),
                  methodsOfUse: fields.methodsOfUse
                    .filter(filterEmptyDescriptions)
                    .map(removeId),
                  procedures: fields.procedures
                    .filter(filterEmptyDescriptions)
                    .map(removeId),
                  easeOfUseRatings: watch('easeOfUseRatings')
                    .filter(filterEmptyDescriptions)
                    .map(removeId),
                  ageGroups: watch('ageGroups')
                    .filter(filterEmptyAgeGroups)
                    .map((group) => ({
                      groupNumber: Number(group.groupNumber),
                      beginAge: Number(group.beginAge),
                      endAge: Number(group.endAge),
                      groupPercentage: Number(group.groupPercentage)
                    })),
                  childrenIds: childJobIds
                };
                const validatedFields = checkMissingFields(dataToSubmit);
                if (validatedFields.isMissingFields) {
                  setMissingFields(validatedFields.missingFields);
                  setShowJobMissingFields(true);
                } else {
                  onSubmit(dataToSubmit);
                }
              })}
            >
              {user?.role === 'admin' ? 'Submit' : 'Save'}
            </Button>
          )}
          {showJobMissingFields && (
            <RequiredFieldsDialog
              hideDialog={() => setShowJobMissingFields(false)}
              missingFields={missingFields}
            />
          )}
        </DialogActions>
      </StyledDialog>
    </>
  );
};

export default JobDialog;
